import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Chip,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  CheckCircle,
  Schedule,
  Delete,
  Mail,
  Edit,
  Error,
  Send,
  Pending,
} from "@mui/icons-material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { Editor } from "@tinymce/tinymce-react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Moment from "moment";
import { toast } from "react-toastify";
import { confirm } from "../confirmation";
import EmailPreview from "../parts/emailPreview";
import { DistributionsApi } from "../../services/api/distributions";
import { SharesApi } from "../../services/api/shares";
import ShareUsageDashboard from "./ShareUsageDashboard";
import { AVATARS_BUCKET } from "../../config";
import { getUserAvatarUrl } from "../../utils/avatarUtils";
import defaultLogo from "../../media/shieldshot_logo_white.png";

Moment.locale("en");

function DistributionDetails(props) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = props;

  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState({
    emails: [],
    subject: "",
    startTime: "",
    status: "",
    total: 0,
    completed: 0,
    failed: 0,
    isScheduled: false,
    percent: 0,
    emailBody: "",
    collections: [],
    progressData: null, // For real-time progress tracking
    distributionStatus: "", // Track current distribution status
  });
  const [toggleSchedule, setToggleSchedule] = useState(false);
  const [time, setTime] = useState(Moment());
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newEmailBody, setNewEmailBody] = useState("");
  const [updatingEmailBody, setUpdatingEmailBody] = useState(false);
  const [addEmail, setAddEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [logoUrl, setLogoUrl] = useState(defaultLogo);
  const [progressPolling, setProgressPolling] = useState(false);
  const [shareCounts, setShareCounts] = useState({}); // email -> { downloadCount, viewCount, shareId }
  const [shareDistId, setShareDistId] = useState(null); // UUID distribution ID for share queries

  const isMounted = useRef(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Helper function to get valid auth token
  const getAuthToken = async () => {
    let authToken = token;
    if (typeof authToken === 'function') {
      authToken = await authToken();
    }
    if (!authToken || typeof authToken !== 'string' || authToken === 'undefined') {
      throw new Error('No valid token found');
    }
    return authToken;
  };

  // Function to get real-time distribution progress
  const getDistributionProgress = async () => {
    try {
      const authToken = await getAuthToken();
      const response = await DistributionsApi.getDistributionProgress(authToken, id);

      // Handle different response structures
      let progressData = null;
      if (response && response.success && response.data) {
        progressData = response.data;
      } else if (response && !response.success && response.data) {
        // Sometimes the data is still in response.data even if success is false
        progressData = response.data;
      } else if (response && typeof response === 'object' && !response.success && !response.data) {
        // Sometimes the response itself is the data
        progressData = response;
      }


      if (progressData) {
        // Update details with progress data
        setDetails(prevDetails => {
          const newCompleted = progressData.sent || progressData.completed || 0;
          const newPercent = progressData.total > 0 ? Math.round((newCompleted / progressData.total) * 100) : 0;

          const updatedDetails = {
            ...prevDetails,
            completed: newCompleted,
            failed: progressData.failed || 0,
            total: progressData.total || prevDetails.total,
            percent: newPercent,
            progressData: progressData
          };

          return updatedDetails;
        });

        // Update email status based on progress data
        if (progressData.emailStatuses) {
          setDetails(prevDetails => ({
            ...prevDetails,
            emails: prevDetails.emails.map(emailItem => ({
              ...emailItem,
              status: progressData.emailStatuses[emailItem.email] || 'pending'
            }))
          }));
        }

        // If all emails are complete but distribution status is still PROCESSING, update it
        console.log('distributionDetails - Checking status update conditions:', {
          allEmailsComplete: progressData.allEmailsComplete,
          distributionStatus: progressData.distributionStatus,
          shouldUpdate: progressData.allEmailsComplete && progressData.distributionStatus === 'PROCESSING'
        });

        if (progressData.allEmailsComplete && progressData.distributionStatus === 'PROCESSING') {
          console.log('distributionDetails - Calling updateDistributionStatus');
          await updateDistributionStatus(authToken, progressData);
        }
      }
    } catch (err) {
      console.error('Failed to fetch distribution progress:', err);
      // Don't show error toast for progress polling failures
    }
  };  // Update distribution status when all emails are complete
  const updateDistributionStatus = async (authToken, progressData) => {
    try {
      console.log('distributionDetails - updateDistributionStatus called with:', {
        progressData,
        failed: progressData.failed,
        total: progressData.total
      });

      const finalStatus = progressData.failed > 0 ? 'FAILED' : 'SUCCESS';
      console.log('distributionDetails - Determined final status:', finalStatus);

      const updateData = {
        relId: id,
        type: 'updateStatus',
        data: {
          distributionStatus: finalStatus
        }
      };

      console.log('distributionDetails - Sending update request:', updateData);

      const updateResponse = await DistributionsApi.updateDistribution(authToken, updateData);
      console.log('distributionDetails - Update response:', updateResponse);

      if (updateResponse.success) {
        console.log(`distributionDetails - Distribution status updated to ${finalStatus}`);
        // Stop polling after successful status update
        setProgressPolling(false);
        // Refresh the details to get the updated status
        setTimeout(() => getDistributionDetails(), 1000);
      }
    } catch (error) {
      console.error('distributionDetails - Failed to update distribution status:', error);
    }
  };

  const getDistributionDetails = async () => {
    try {
      const authToken = await getAuthToken();

      const response = await DistributionsApi.getDistributionDetails(authToken, id);

      if (!response.success) {
        if (response.status === 404) navigate("/404");
        throw new Error("Failed to fetch distribution details");
      }

      const responseJson = response.data;
      if (!isMounted.current) return;

      if (!responseJson.length) {
        navigate("/404");
        return;
      }

      let completed = 0;
      let distributionIsScheduled = false;
      const collectionsMap = new Map();

      responseJson.forEach((item) => {
        if (item.distributionStatus === "SUCCESS") completed++;
        if (item.distributionStatus === "SCHEDULED") distributionIsScheduled = true;        // Parse setInfo - it's now a JSON string
        if (item.setInfo) {
          try {
            const setInfoArray = typeof item.setInfo === 'string' ? JSON.parse(item.setInfo) : item.setInfo;
            setInfoArray.forEach((set) => {
              const setName = set.name || 'Unknown Set';
              const setRelId = set.relId || -1;
              if (!collectionsMap.has(setName)) {
                collectionsMap.set(setName, setRelId);
              }
            });
          } catch (error) {
            console.error('Failed to parse setInfo:', error);
          }
        }
      });

      const firstItem = responseJson[0];

      // Transform emails array to match expected format for the list component
      const emailsWithStatus = (firstItem.emails || []).map(email => ({
        email: typeof email === 'string' ? email : email.S || email,
        distributionStatus: firstItem.distributionStatus
      }));

      const isScheduled = firstItem.distributionStatus === "SCHEDULED";
      const isProcessing = firstItem.distributionStatus === "PROCESSING";
      const isCompleted = firstItem.distributionStatus === "SUCCESS";
      const isFailed = firstItem.distributionStatus === "FAILED";

      const initialDetails = {
        emails: emailsWithStatus,
        subject: firstItem.subject,
        startTime: Moment.unix(firstItem.startTime).format("YYYY/MM/DD hh:mm a"),
        total: firstItem.emails ? firstItem.emails.length : 0,
        completed: isCompleted ? firstItem.emails.length : 0,
        failed: isFailed ? firstItem.emails.length : 0,
        isScheduled: isScheduled,
        percent: isCompleted ? 100 : 0,
        emailBody: firstItem.message || "",
        collections: Array.from(collectionsMap.entries()),
        progressData: null,
        distributionStatus: firstItem.distributionStatus
      };

      console.log('distributionDetails - Setting initial details:', {
        distributionStatus: initialDetails.distributionStatus,
        total: initialDetails.total,
        completed: initialDetails.completed,
        failed: initialDetails.failed,
        percent: initialDetails.percent,
        isProcessing
      });

      setDetails(initialDetails);
      // Store the UUID distribution ID for share queries (route param is relId, not UUID)
      const distUuid = firstItem.id || firstItem.distributionId;
      if (distUuid) setShareDistId(distUuid);
      // Fetch per-email share counts for this distribution
      try {
        const sharesResp = await SharesApi.getByDistribution(authToken, firstItem.id || firstItem.distributionId);
        if (sharesResp?.success && Array.isArray(sharesResp.data?.items)) {
          const map = {};
          for (const it of sharesResp.data.items) {
            map[it.email] = { downloadCount: it.downloadCount || 0, viewCount: it.viewCount || 0, shareId: it.shareId };
          }
          setShareCounts(map);
        }
      } catch (e) {
        console.warn('Failed to fetch shares for distribution', e);
      }
      setNewEmailBody(firstItem.message);

      // Start progress polling if distribution is in progress
      if (isProcessing && !progressPolling) {
        console.log('distributionDetails - Starting progress polling');
        setProgressPolling(true);
        // Also fetch initial progress data immediately
        setTimeout(() => getDistributionProgress(), 100);
      } else if ((!isProcessing || isCompleted || isFailed) && progressPolling) {
        console.log('distributionDetails - Stopping progress polling');
        setProgressPolling(false);
      }

      setLoading(false);
    } catch (err) {
      console.error(err);
      if (err.message === 'No valid token found') {
        toast.error('Authentication error. Please log in again.');
      } else {
        toast.error('Failed to load distribution details.');
      }
      if (isMounted.current) setLoading(false);
    }
  };

  useEffect(() => {
    isMounted.current = true;

    // Load user avatar URL with fallback
    const loadAvatarUrl = async () => {
      if (user?.sub) {
        const resolvedAvatarUrl = await getUserAvatarUrl(user.sub);
        if (isMounted.current) {
          setAvatarUrl(resolvedAvatarUrl);
        }
      }
    };

    loadAvatarUrl();
    getDistributionDetails();

    // Set up main polling interval
    const mainIntervalID = setInterval(getDistributionDetails, 30000); // Every 30 seconds for general updates

    return () => {
      isMounted.current = false;
      clearInterval(mainIntervalID);
    };
  }, [id, user?.sub]);

  // Separate useEffect for progress polling when distribution is in progress
  useEffect(() => {
    let progressIntervalID = null;

    // Only poll if distribution is actively processing
    // Don't rely on progressData.allEmailsComplete to stop polling to avoid race conditions
    const shouldPoll = progressPolling && details.distributionStatus === 'PROCESSING';

    console.log('distributionDetails - Polling useEffect triggered:', {
      progressPolling,
      distributionStatus: details.distributionStatus,
      shouldPoll
    });

    if (shouldPoll && isMounted.current) {
      // More frequent polling for active distributions
      progressIntervalID = setInterval(getDistributionProgress, 5000); // Every 5 seconds
      // Also get initial progress data
      getDistributionProgress();
    }

    return () => {
      if (progressIntervalID) {
        clearInterval(progressIntervalID);
      }
    };
  }, [progressPolling, id, details.distributionStatus]);

  // Monitor distribution status changes and stop polling when complete
  useEffect(() => {
    if (details.distributionStatus &&
      ['SUCCESS', 'FAILED', 'CANCELLED'].includes(details.distributionStatus) &&
      progressPolling) {
      console.log(`Distribution ${details.distributionStatus}, stopping progress polling`);
      setProgressPolling(false);
    }
  }, [details.distributionStatus, progressPolling]);

  const handleCancelDistribution = async () => {
    if (await confirm("Are you sure you want to cancel this distribution?")) {
      try {
        const authToken = await getAuthToken();
        const response = await DistributionsApi.updateDistribution(authToken, {
          relId: id,
          type: "cancel"
        });
        if (response.success) {
          toast.success("Distribution cancelled.");
          navigate("/distributions");
        } else {
          toast.error("Failed to cancel distribution: " + (response.message || 'Unknown error'));
        }
      } catch (err) {
        toast.error("Failed to cancel distribution.");
      }
    }
  };

  const handleUpdateEmailBody = async () => {
    setUpdatingEmailBody(true);
    try {
      const authToken = await getAuthToken();
      const response = await DistributionsApi.updateDistribution(authToken, {
        relId: id,
        type: "emailBody",
        data: newEmailBody
      });
      if (response.success) {
        toast.success("Email body updated");
        setDetails(prev => ({ ...prev, emailBody: newEmailBody }));
        setShowEditModal(false);
      } else {
        toast.error("Failed to update email body: " + (response.message || 'Unknown error'));
      }
    } catch (err) {
      toast.error("Failed to update email body.");
    } finally {
      setUpdatingEmailBody(false);
    }
  };

  const handleChangeSchedule = async () => {
    try {
      const authToken = await getAuthToken();
      const response = await DistributionsApi.updateDistribution(authToken, {
        relId: id,
        type: "updateSchedule",
        data: { startTime: Moment(time).unix() }
      });
      if (response.success) {
        toast.success("Schedule changed");
        setDetails(prev => ({ ...prev, startTime: Moment(time).format("YYYY/MM/DD hh:mm a") }));
        setToggleSchedule(false);
      } else {
        toast.error("Failed to update schedule: " + (response.message || 'Unknown error'));
      }
    } catch (err) {
      toast.error("Failed to update schedule.");
    }
  };

  const handleDeleteEmail = async (email, index) => {
    try {
      const authToken = await getAuthToken();
      const response = await DistributionsApi.updateDistribution(authToken, {
        relId: id,
        type: "deleteEmail",
        data: email
      });
      if (response.success) {
        setDetails(prev => ({ ...prev, emails: prev.emails.filter((_, i) => i !== index) }));
      } else {
        toast.error("Failed to delete email: " + (response.message || 'Unknown error'));
      }
      toast.success("Email deleted");
    } catch (err) {
      toast.error("Failed to delete email.");
    }
  };

  // Helper function to get status icon and color for emails
  const getEmailStatusIcon = (status, errorMessage) => {
    switch (status) {
      case "COMPLETED":
      case "SUCCESS":
        return <CheckCircle color="success" />;
      case "FAILED":
      case "ERROR":
        return <Error color="error" titleAccess={errorMessage || "Failed"} />;
      case "PROCESSING":
        return <Send color="info" />;
      case "QUEUED":
        return <Schedule color="action" />;
      case "SCHEDULED":
        return <Schedule color="warning" />;
      case "PENDING":
        return <Pending color="action" />;
      default:
        return <Schedule color="warning" />;
    }
  };

  const handleAddEmail = async (e) => {
    e.preventDefault();
    try {
      const authToken = await getAuthToken();
      const response = await DistributionsApi.updateDistribution(authToken, {
        relId: id,
        type: "addEmail",
        data: newEmail
      });
      if (response.success) {
        setDetails(prev => ({
          ...prev,
          emails: [...prev.emails, { email: newEmail, distributionStatus: "SCHEDULED" }]
        }));
        setNewEmail("");
        setAddEmail(false);
        toast.success("Email added");
      } else {
        toast.error("Failed to add email: " + (response.error === "Email already exists in distribution" ? response.error : 'Unknown error'));
      }
    } catch (err) {
      toast.error("Failed to add email.");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: isMobile ? "95vw" : "100%",
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-center",
        bgcolor: "background.default",
        px: { xs: 2, sm: 2, md: 2 },
        py: { xs: 0, sm: 2, md: 2 },
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 900,
          mx: "auto",
        }}
      >
        {/* Edit Email Modal */}
        <Dialog fullWidth maxWidth="lg" open={showEditModal} onClose={() => setShowEditModal(false)}>
          <DialogTitle>Edit Email Body</DialogTitle>
          <DialogContent>
            <Editor
              apiKey="er0sqezegew4ztzey6vtavdg77pjxd49j8l34vub9amk48c2"
              value={newEmailBody}
              init={{
                height: 500,
                menubar: false,
                plugins: "advlist autolink lists link image charmap print preview anchor searchreplace visualblocks code fullscreen insertdatetime media table paste code help",
                toolbar: "undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help",
              }}
              onEditorChange={(content) => setNewEmailBody(content)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={handleUpdateEmailBody} variant="contained" disabled={updatingEmailBody}>
              {updatingEmailBody ? <CircularProgress size={24} /> : "Save Changes"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Preview Email Modal */}
        <Dialog fullWidth maxWidth="md" open={showPreviewModal} onClose={() => setShowPreviewModal(false)}>
          <DialogTitle>
            Email Preview
            {details.isScheduled && (
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => {
                  setShowPreviewModal(false);
                  setShowEditModal(true);
                }}
                sx={{ ml: 2 }}
              >
                Edit Email Body
              </Button>
            )}
          </DialogTitle>
          <DialogContent dividers>
            <EmailPreview
              logoUrl={logoUrl}
              avatarUrl={avatarUrl || `https://${AVATARS_BUCKET}.s3.amazonaws.com/default.png`}
              username={user.username}
              body={details.emailBody}
            />
          </DialogContent>
        </Dialog>

        {/* Main Content */}
        <Paper sx={{
          p: { xs: 2, sm: 4 },
          borderRadius: { xs: 1, sm: 2 },
          boxShadow: 2,
          mt: 3,
          mx: { xs: 0, sm: 0 }
        }}>
          <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 600, color: "primary.main" }}>
            {details.subject}
          </Typography>

          <Box sx={{ mb: 4 }}>
            {details.percent === 100 && (details.distributionStatus === 'SUCCESS' || !details.distributionStatus) ? (
              <Chip
                icon={<CheckCircle />}
                label={`Distribution Complete (${details.completed} emails sent)`}
                color="success"
                size="large"
              />
            ) : details.distributionStatus === 'FAILED' || details.failed > 0 ? (
              <Chip
                icon={<Error />}
                label={`Distribution Failed (${details.completed} sent, ${details.failed} failed)`}
                color="error"
                size="large"
              />
            ) : details.isScheduled ? (
              <Chip
                icon={<Schedule />}
                label={`Scheduled on ${details.startTime}`}
                color="warning"
                size="large"
              />
            ) : progressPolling ? (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ flex: 1, mr: 2 }}>
                    <LinearProgress variant="determinate" value={details.percent} sx={{ height: 8, borderRadius: 4 }} />
                  </Box>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Processing: {Math.round(details.percent)}% complete
                  ({details.completed} sent, {details.failed > 0 ? `${details.failed} failed, ` : ''}{details.total - details.completed - details.failed} pending)
                </Typography>
                {details.progressData?.estimatedCompletion && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Estimated completion: {new Date(details.progressData.estimatedCompletion).toLocaleTimeString()}
                  </Typography>
                )}
              </Box>
            ) : (
              <Box>
                <LinearProgress variant="determinate" value={details.percent} sx={{ mb: 1, height: 8, borderRadius: 4 }} />
                <Typography variant="body2" color="text.secondary">
                  Progress: {Math.round(details.percent)}% ({details.completed} of {details.total} completed)
                  {details.failed > 0 && `, ${details.failed} failed`}
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: { xs: 1, sm: 2 },
            alignItems: 'center',
            justifyContent: { xs: 'stretch', sm: 'center' },
            minHeight: '56px',
            '& > *': {
              flexGrow: { xs: 1, sm: 0 },
              minWidth: { xs: 'auto', sm: 'auto' }
            }
          }}>
            <Button
              variant="outlined"
              startIcon={<Mail />}
              onClick={() => setShowPreviewModal(true)}
              sx={{ height: '40px' }}
            >
              View Mail
            </Button>
            {details.isScheduled && (
              <>
                {toggleSchedule ? (
                  <>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DateTimePicker
                        label="Schedule Time"
                        value={time}
                        onChange={(newValue) => setTime(newValue)}
                        slots={{
                          textField: TextField
                        }}
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: { minWidth: { xs: '140px', sm: '200px' }, maxWidth: '100%' }
                          }
                        }}
                        minDateTime={Moment()}
                      />
                    </LocalizationProvider>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleChangeSchedule}
                      sx={{ height: '40px' }}
                    >
                      Save
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outlined"
                    onClick={() => setToggleSchedule(true)}
                    sx={{ height: '40px' }}
                  >
                    Change Schedule
                  </Button>
                )}
                {!toggleSchedule && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleCancelDistribution}
                    sx={{ height: '40px' }}
                  >
                    Cancel Distribution
                  </Button>
                )}
              </>
            )}
          </Box>
        </Paper>

        {/* Collections Section */}
        <Paper sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: { xs: 1, sm: 2 },
          boxShadow: 2,
          mt: 3,
          mx: { xs: 0, sm: 0 }
        }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "primary.main" }}>
            Collections
          </Typography>
          <Box>
            {details.collections.map(([name, psId], index) => (
              <Button
                key={index}
                variant="outlined"
                size="medium"
                component={psId > 0 ? Link : "button"}
                to={`/collections/${psId}`}
                sx={{ mr: 1, mb: 1 }}
              >
                {name}
              </Button>
            ))}
          </Box>
        </Paper>

        {/* Email List Section */}
        <Paper sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: { xs: 1, sm: 2 },
          boxShadow: 2,
          mt: 3,
          mx: { xs: 0, sm: 0 }
        }}>
          <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Grid item>
              <Typography variant="h6" sx={{ fontWeight: 600, color: "primary.main" }}>
                Email List ({details.emails.length})
              </Typography>
            </Grid>
            <Grid item>
              {addEmail ? (
                <form onSubmit={handleAddEmail}>
                  <TextField
                    type="email"
                    label="New Email"
                    size="small"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    required
                  />
                  <Button type="submit" variant="contained" sx={{ ml: 1 }}>Add</Button>
                </form>
              ) : details.isScheduled && (
                <Button variant="contained" onClick={() => setAddEmail(true)}>
                  Add Email
                </Button>
              )}
            </Grid>
          </Grid>

          <List sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            {details.emails.map((item, index) => (
              <ListItem key={index} divider={index < details.emails.length - 1}>
                <ListItemIcon>
                  {getEmailStatusIcon(item.distributionStatus, item.errorMessage)}
                </ListItemIcon>
                <ListItemText
                  primary={item.email}
                  secondary={
                    item.errorMessage ? (
                      <Typography variant="caption" color="error">
                        Error: {item.errorMessage}
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        {item.distributionStatus && item.distributionStatus !== "SCHEDULED" && (
                          <Typography variant="caption" color="text.secondary">
                            Status: {item.distributionStatus}
                          </Typography>
                        )}
                        {shareCounts[item.email] && (
                          <Typography variant="caption" color="text.secondary">
                            Downloads: {shareCounts[item.email].downloadCount} · Views: {shareCounts[item.email].viewCount}
                          </Typography>
                        )}
                      </Box>
                    )
                  }
                />
                {(item.distributionStatus === "SCHEDULED" || details.isScheduled) && (
                  <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteEmail(item.email, index)}>
                    <Delete />
                  </IconButton>
                )}
              </ListItem>
            ))}
          </List>
        </Paper>

        <ShareUsageDashboard token={token} distributionId={shareDistId} />
      </Box>
    </Box>
  );
}

export default DistributionDetails;
